import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { getEmploymentTypeTranslationKey } from '@/constants/employmentTypeI18n';
import { ADMIN_ROLE, CLIENT_ROLE, RECRUITER_ROLE } from '@/constants/roles';
import { useEmploymentTypesQuery } from '@/hooks/queries/useEmploymentTypesQuery';
import { useCurrentUser } from '@/hooks/useCurrentUser';

import type { EmploymentType } from '@/types/employmentType';

/**
 * Employment types from React Query — no app-wide Context.
 * Deduped via `queryKeys.employmentTypes.list()`.
 */
export function useEmploymentTypes(): EmploymentType[] {
  const { isAuth, user } = useCurrentUser();
  const shouldLoadEmploymentTypes =
    isAuth &&
    (user?.role === CLIENT_ROLE ||
      user?.role === RECRUITER_ROLE ||
      user?.role === ADMIN_ROLE);
  const { data: employmentTypes = [] } = useEmploymentTypesQuery({
    enabled: shouldLoadEmploymentTypes,
  });
  return employmentTypes;
}

export const useGetEmploymentTypeNameById = () => {
  const { t } = useTranslation();

  return useCallback(
    (id: string) => {
      const translationKey = getEmploymentTypeTranslationKey(id);
      const translatedName = t(translationKey);
      return translatedName !== translationKey
        ? translatedName
        : translationKey;
    },
    [t],
  );
};
